import { notification } from "antd";
import {
  ensureFileSession,
  isTrustedApiRequest,
  shouldPersistAuthResponse,
} from "./fileAccess";
import axios, { AxiosRequestConfig } from "axios";
import store from "@lhd/app/store";
import { userLogout } from "@lhd/features/users/usersSlice";
import {
  getAccessToken,
  getClient,
  getCompanyId,
  getUid,
  removeCookie,
  setCookie,
} from "@lhd/utility";
import { setAxiosLoading } from "@lhd/hooks";
import { disconnectSharedCable } from "./actionCable";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const persistAuthHeaders = (
  headers?: Record<string, any>,
  requestClient?: unknown,
  signIn = false,
  requestUid?: unknown,
) => {
  if (!shouldPersistAuthResponse(requestClient, signIn, requestUid))
    return false;
  const previousToken = getAccessToken();
  const accessToken = headers?.["access-token"];
  const client = headers?.client;
  const uid = headers?.uid;
  const expiry = headers?.expiry;

  const currentExpiry = localStorage.getItem("expiry");
  if (expiry && currentExpiry) {
    if (Number(expiry) < Number(currentExpiry)) {
      return;
    }
  }

  if (accessToken) setCookie("access_token", accessToken);
  if (client) localStorage.setItem("client", client);
  if (uid) localStorage.setItem("uid", uid);
  if (expiry) localStorage.setItem("expiry", expiry);
  return Boolean(accessToken && accessToken !== previousToken);
};

// Add a request interceptor
axiosInstance.interceptors.request.use(
  async function (config: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    const trustedApi = isTrustedApiRequest(config.url, config.baseURL);
    // Track loading start with URL
    const url = config.url || "";
    setAxiosLoading(true, url);

    try {
      if (trustedApi && !/\/auth(?:\/|$)/.test(config.url || ""))
        await ensureFileSession();
    } catch (error) {
      // Preserve the request context so the existing error interceptor balances
      // its loading state and handles an expired session in the usual way.
      throw Object.assign(
        error instanceof Error
          ? error
          : new Error("File authentication failed"),
        { config },
      );
    }

    if (config.headers) {
      config.headers = {
        ...config.headers,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      };

      if (getAccessToken() && trustedApi) {
        config.headers = {
          ...config.headers,
          "access-token": getAccessToken(),
          client: getClient(),
          uid: getUid(),
          "ngrok-skip-browser-warning": "true",
          "X-Login-Company-Id": getCompanyId(),
        };
      }
    }
    return config;
  },

  function (error) {
    // Track loading end on error
    const url = error?.config?.url || "";
    setAxiosLoading(false, url);
    return Promise.reject(error);
  },
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
  async function (response) {
    // Track loading end on success with URL
    const url = response?.config?.url || "";
    setAxiosLoading(false, url);
    const trustedResponse = isTrustedApiRequest(
      response.config.url,
      response.config.baseURL,
    );
    const signIn =
      trustedResponse &&
      response.config.method === "post" &&
      /\/auth\/sign_in(?:\?|$)/.test(response.config.url || "");
    const updated =
      trustedResponse &&
      persistAuthHeaders(
        response?.headers,
        response.config.headers?.client,
        signIn,
        response.config.headers?.uid,
      );
    if (updated && !signIn) {
      try {
        await ensureFileSession(true);
      } catch {
        // The business API already succeeded. Do not report a saved write as a
        // failure and encourage a duplicate submission because renewal failed.
        notification.warning({
          key: "file-access-auth",
          message:
            "ファイルを開く準備ができませんでした。接続を確認し、必要に応じて再読み込みしてください。",
        });
      }
    }

    return response;
  },

  function (error) {
    // Track loading end on error with URL
    const url = error?.config?.url || "";
    setAxiosLoading(false, url);

    // notification["error"]({
    //   message: "保存に失敗しました。",
    // });
    if (error?.response?.status === 401) {
      disconnectSharedCable();
      store.dispatch(userLogout());
      localStorage.clear();
      removeCookie("access_token");
      if (window.location.pathname !== "/login") {
        window.location.replace("/login");
      }
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
